import React, { useEffect, useRef } from "react";
import { Alert, AlertTitle, Box, Button } from "@mui/material";

import { Progress } from "ui";

import analytics from "fond/analytics";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { Maybe, WORKFLOW_KEYS } from "fond/types";

interface BomCarvePanelProps {
  isDisabled?: boolean;
  versionId: Maybe<string>;
}

const stageConfig: Record<string, { label: string }> = {
  partition: { label: "Partitioning BOM" },
};

export const BomCarvePanel: React.FC<BomCarvePanelProps> = ({ versionId, isDisabled }) => {
  const { workflowState, stages, runWorkflow } = useCustomWorkflow({
    versionId: versionId ?? undefined,
    key: WORKFLOW_KEYS.COLLABORATION_BOM_CARVE.ROOT,
  });

  const isRunning = workflowState === "STARTING" || workflowState === "IN_PROGRESS";
  const prevWorkflowStateRef = useRef<typeof workflowState>();
  // Track workflow completion
  useEffect(() => {
    if (prevWorkflowStateRef.current && prevWorkflowStateRef.current !== workflowState) {
      if (versionId && (workflowState === "COMPLETE" || workflowState === "ERROR")) {
        analytics.track("collaboration.cts.bom-carve.completed", {
          versionId,
          status: workflowState,
        });
      }
    }
    prevWorkflowStateRef.current = workflowState;
  }, [workflowState, versionId]);

  const handleStartCarveBOM = async () => {
    if (versionId) {
      analytics.track("collaboration.cts.bom-carve.started", {
        versionId,
        isRegenerate: workflowState === "COMPLETE",
      });
    }
    runWorkflow?.({
      PartitionLayer: {
        LayerKey: "PN Boundaries",
        IDAttribute: { Name: "PN" },
      },
    });
  };

  return (
    <>
      <Box mt={2} mb={2}>
        {workflowState === "COMPLETE" && (
          <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
            Carved BOM outputs generated.
          </Alert>
        )}

        {workflowState === "ERROR" && (
          <Alert severity="error" variant="compact" sx={{ mb: 2 }}>
            <AlertTitle>Carve BOM failed</AlertTitle>
            Carved BOM outputs could not be generated.
          </Alert>
        )}

        {(isRunning || workflowState === "ERROR") && (
          <>
            {isRunning && (
              <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
                Generating carved BOM outputs.
              </Alert>
            )}
            {stages ? (
              <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
            ) : (
              <Progress steps={[{ ID: "preparing", Label: "Preparing", Status: "InProgress" }]} />
            )}
          </>
        )}
      </Box>
      {!isRunning && (
        <Button fullWidth variant="contained" size="small" disabled={!runWorkflow || isRunning || isDisabled} onClick={handleStartCarveBOM}>
          {workflowState === "COMPLETE" ? "Recarve" : "Carve"} BOM by cabinet
        </Button>
      )}
    </>
  );
};
